'use client';

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
	FloatingArrow,
	FloatingFocusManager,
	FloatingPortal,
	arrow,
	autoUpdate,
	flip,
	hide,
	offset,
	shift,
	useDismiss,
	useFloating,
	useInteractions,
} from '@floating-ui/react';
import { X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import Boss from '../appsIcons/Boss';
import { Button } from '../Button';
import { useActionBarAberta } from '../ActionBar';
import { useTranslation } from '../../lib/i18n/TranslationContext';
import { useSlots } from '../../context/ComponentesSlots';
import { useSubMenu } from '../../SubMenuContext';
import { rolarCadeiaAteAVista } from '../../components/Slots/rolagem';
import {
	abandonTour,
	dismissTour,
	getActiveTour,
	markStepShown,
	nextStep,
	precarregarVideo,
	previousStep,
	resolverAlvo,
	skipStep,
	useTour,
	useTourExplicita,
	useTourExplicitas,
	useTourTarget,
	type TourStep,
	type TourTargetRef,
} from './tourStore';

export {
	getActiveTour,
	isTourDismissed,
	resetTour,
	showTip,
	startTour,
	stopTour,
	useTour,
	type TipDefinition,
	type TourDefinition,
	type TourOptions,
	type TourPlacement,
	type TourStep,
} from './tourStore';

const ESPERA_ALVO = 3000;
const TETO_ESTAVEL = 600;
const QUADROS_ESTAVEIS = 3;
const TOLERANCIA_SUMIU = 300;
const CHECAGEM = 250;
const DISTANCIA = 12;
const RECUO_DA_SETA = 14;
const RECUO_TELA = 12;
const FAIXA_DA_ACTION_BAR = 64;
const ESPACO_ACTION_BAR = FAIXA_DA_ACTION_BAR + RECUO_TELA;
const EASE = [0.23, 1, 0.32, 1] as const;

const ORIGEM: Record<string, string> = {
	top: 'bottom center',
	bottom: 'top center',
	left: 'center right',
	right: 'center left',
};

const quadro = () => new Promise<number>((resolve) => requestAnimationFrame(resolve));

const esperarAlvo = async (target: TourTargetRef, cancelado: () => boolean) => {
	const limite = performance.now() + ESPERA_ALVO;

	while (!cancelado()) {
		const el = resolverAlvo(target);

		if (el) return el;
		if (performance.now() > limite) return null;

		await quadro();
	}

	return null;
};

const mesmoRetangulo = (a: DOMRect, b: DOMRect) =>
	Math.abs(a.top - b.top) < 0.5 &&
	Math.abs(a.left - b.left) < 0.5 &&
	Math.abs(a.width - b.width) < 0.5 &&
	Math.abs(a.height - b.height) < 0.5;

const esperarParar = async (el: Element, cancelado: () => boolean) => {
	const limite = performance.now() + TETO_ESTAVEL;
	let anterior = el.getBoundingClientRect();
	let estaveis = 0;

	while (!cancelado() && performance.now() < limite) {
		await quadro();

		const atual = el.getBoundingClientRect();
		estaveis = mesmoRetangulo(atual, anterior) ? estaveis + 1 : 0;

		if (estaveis >= QUADROS_ESTAVEIS) return;

		anterior = atual;
	}
};

const podeUsarSetas = (el: EventTarget | null) =>
	el === document.body || (el instanceof Element && el.closest('[data-empreender-tour-card]') !== null);

function VideoDoPasso({ url }: { url: string }) {
	const semMovimento = useReducedMotion() ?? false;
	const caixa = useRef<HTMLDivElement>(null);
	const [pronto, setPronto] = useState(false);

	useEffect(() => {
		const video = precarregarVideo(url);
		const destino = caixa.current;

		if (!video || !destino) return;

		video.className = 'block size-full rounded-xl object-cover pointer-events-none';
		destino.appendChild(video);

		const marcarPronto = () => setPronto(true);

		video.addEventListener('loadeddata', marcarPronto);

		if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) marcarPronto();
		if (!semMovimento) video.play().catch(() => undefined);

		return () => {
			video.removeEventListener('loadeddata', marcarPronto);
			video.pause();
			video.currentTime = 0;
			video.remove();
		};
	}, [url, semMovimento]);

	return (
		<div
			ref={caixa}
			aria-hidden
			className={twMerge('size-full transition-opacity duration-200', pronto ? 'opacity-100' : 'opacity-0')}
		/>
	);
}

type AbrirPainel = (menu?: string, slot?: string) => void;

type CardProps = {
	alvo: Element;
	passo: TourStep;
	index: number;
	total: number;
	unico: boolean;
	focar: boolean;
	aoNavegar: (focoDentro: boolean) => void;
};

function CardDoPasso({ alvo, passo, index, total, unico, focar, aoNavegar }: CardProps) {
	const { t } = useTranslation();
	const semMovimento = useReducedMotion();
	const barraAberta = useActionBarAberta();
	const idTitulo = useId();
	const idDescricao = useId();
	const cartao = useRef<HTMLDivElement>(null);
	const seta = useRef<SVGSVGElement>(null);

	useEffect(() => {
		if (focar) cartao.current?.querySelector<HTMLButtonElement>('[data-tour-primario]')?.focus();
	}, [focar]);

	const navegar = (acao: () => void) => () => {
		aoNavegar(cartao.current?.contains(document.activeElement) ?? false);
		acao();
	};

	const folga = {
		top: RECUO_TELA,
		left: RECUO_TELA,
		right: RECUO_TELA,
		bottom: RECUO_TELA + (barraAberta ? ESPACO_ACTION_BAR : 0),
	};

	const { refs, floatingStyles, context, placement, middlewareData } = useFloating({
		open: true,
		onOpenChange: (abrir) => {
			if (!abrir) dismissTour();
		},
		elements: { reference: alvo },
		placement: passo.placement ?? 'bottom',
		strategy: 'fixed',
		whileElementsMounted: autoUpdate,
		middleware: [
			offset(DISTANCIA),
			flip({ padding: folga }),
			shift({ padding: folga }),
			arrow({ element: seta, padding: RECUO_DA_SETA }),
			hide(),
		],
	});

	const dispensar = useDismiss(context, { outsidePress: false });
	const { getFloatingProps } = useInteractions([dispensar]);

	const ultimo = index === total - 1;
	const escondido = middlewareData.hide?.referenceHidden === true;
	const escala = semMovimento ? 1 : 0.96;

	return (
		<FloatingFocusManager context={context} modal={false} initialFocus={-1} returnFocus={false} closeOnFocusOut={false}>
			<div
				ref={refs.setFloating}
				style={{ ...floatingStyles, visibility: escondido ? 'hidden' : undefined }}
				className='z-[60]'
				{...getFloatingProps()}>
				<motion.div
					ref={cartao}
					role='dialog'
					aria-labelledby={idTitulo}
					aria-describedby={passo.description ? idDescricao : undefined}
					data-empreender-tour-card=''
					initial={{ opacity: 0, scale: escala }}
					animate={{ opacity: 1, scale: 1 }}
					exit={{ opacity: 0, scale: escala }}
					transition={{ duration: 0.2, ease: EASE }}
					style={{ transformOrigin: ORIGEM[placement.split('-')[0]] }}
					className='relative gap-4 flex flex-col w-[300px] max-w-[calc(100vw-24px)] rounded-xl bg-white p-4 shadow-[0_4px_28px_0_rgba(0,0,0,0.15),1px_0_0_0_rgba(0,0,0,0.10),0_-1px_0_0_rgba(0,0,0,0.10),0_1px_0_0_rgba(0,0,0,0.10),-1px_0_0_0_rgba(0,0,0,0.10)]'>
					<div
						className={twMerge(
							'relative flex h-[152px] w-[268px] max-w-full items-center justify-center overflow-hidden rounded-xl',
							passo.video ? null : 'bg-[#8346F9]',
						)}>
						{passo.video ? (
							<VideoDoPasso url={passo.video} />
						) : (
							<div className='flex size-[90px] items-center justify-center rounded-[20px] bg-white'>
								<Boss className='size-[60px] text-black' />
							</div>
						)}
					</div>

					<button
						type='button'
						aria-label={t('Fechar')}
						onClick={dismissTour}
						className='absolute right-6 top-6 flex size-8 items-center justify-center rounded-full bg-gray-900/45 text-white outline-none transition hover:bg-gray-900/60 focus-visible:ring-2 focus-visible:ring-white/80 active:scale-95'>
						<X className='size-4 shrink-0' strokeWidth={2.2} />
					</button>

					<div className={twMerge('text-center flex flex-col gap-2')}>
						<p id={idTitulo} className="text-[#111827] text-center font-['Inter'] text-xs not-italic font-medium leading-4 tracking-normal">
							{passo.title}
						</p>

						{passo.description ? (
							<p id={idDescricao} className="text-[#6B7280] text-center font-['Inter'] text-xs font-normal leading-4 tracking-normal">
								{passo.description}
							</p>
						) : null}
					</div>

					<div className={twMerge('mt-4 flex items-center gap-2', unico ? 'justify-end' : 'justify-between')}>
						{unico ? null : (
							<Button variant='secondary' disabled={index === 0} onClick={navegar(previousStep)} className='h-9 px-3'>
								{t('Voltar')}
							</Button>
						)}

						{unico ? null : (
							<span className="text-[#111827] font-['Inter'] text-xs font-medium leading-4 tracking-normal">
								{index + 1} {t('de')} {total}
							</span>
						)}

						<Button data-tour-primario='' onClick={navegar(nextStep)} className='h-9 px-3'>
							{unico ? t('Entendi') : ultimo ? t('Concluir') : t('Próximo')}
						</Button>
					</div>

					<FloatingArrow
						ref={seta}
						context={context}
						width={7}
						height={5}
						tipRadius={1}
						fill='#fff'
						stroke='rgba(0,0,0,0.10)'
						strokeWidth={1}
					/>
				</motion.div>
			</div>
		</FloatingFocusManager>
	);
}

const chaves = new WeakMap<object, number>();
let ultimaChave = 0;

const chaveDe = (ativo: object) => {
	let chave = chaves.get(ativo);

	if (chave === undefined) {
		ultimaChave += 1;
		chave = ultimaChave;
		chaves.set(ativo, chave);
	}

	return chave;
};

function TourUI({ abrirPainel }: { abrirPainel?: AbrirPainel }) {
	const { tour, index, total, unico } = useTour();
	const ativo = getActiveTour();
	const passo = tour?.steps[index];
	const [ancora, setAncora] = useState<{ de: object; el: Element } | null>(null);
	const alvo = ativo && ancora?.de === ativo ? ancora.el : null;
	const focoNoCard = useRef(false);
	const tourAnterior = useRef<string | null>(null);
	const [modalAberto, setModalAberto] = useState(false);

	const abrir = useRef(abrirPainel);
	abrir.current = abrirPainel;

	const aoNavegar = (focoDentro: boolean) => {
		focoNoCard.current = focoDentro;
	};

	useEffect(() => {
		setAncora(null);

		if (tourAnterior.current !== (tour?.id ?? null)) {
			tourAnterior.current = tour?.id ?? null;
			focoNoCard.current = false;
		}

		if (!ativo || !tour || !passo) return;

		let cancelado = false;
		const foiCancelado = () => cancelado;

		if (passo.menu || passo.slot) abrir.current?.(passo.menu, passo.slot);

		const levar = async () => {
			const el = await esperarAlvo(passo.target, foiCancelado);

			if (cancelado) return;

			if (!el) {
				console.warn(`[empreender-ui] tour "${tour.id}": o alvo do passo ${index + 1} não apareceu — passo pulado.`);
				skipStep();
				return;
			}

			await esperarParar(el, foiCancelado);
			if (cancelado) return;

			rolarCadeiaAteAVista(el, { janela: true });

			await esperarParar(el, foiCancelado);
			if (cancelado) return;

			setAncora({ de: ativo, el });
			markStepShown(index);
		};

		levar();

		return () => {
			cancelado = true;
		};
	}, [ativo, tour, passo, index]);

	useEffect(() => {
		if (!ativo || !alvo || !passo) return;

		let sumiuEm = 0;

		const conferir = window.setInterval(() => {
			const atual = resolverAlvo(passo.target);

			if (atual) {
				sumiuEm = 0;
				if (atual !== alvo) setAncora({ de: ativo, el: atual });
				return;
			}

			if (!sumiuEm) {
				sumiuEm = performance.now();
				return;
			}

			if (performance.now() - sumiuEm >= TOLERANCIA_SUMIU) abandonTour();
		}, CHECAGEM);

		return () => window.clearInterval(conferir);
	}, [ativo, alvo, passo]);

	useEffect(() => {
		if (!alvo || unico) return;

		const setas = (e: KeyboardEvent) => {
			if (e.defaultPrevented || !podeUsarSetas(e.target)) return;
			if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;

			focoNoCard.current = e.target !== document.body;

			if (e.key === 'ArrowRight') nextStep();
			else previousStep();
		};

		window.addEventListener('keydown', setas);

		return () => window.removeEventListener('keydown', setas);
	}, [alvo, unico]);

	useEffect(() => {
		const conferir = () => setModalAberto(document.body.hasAttribute('data-scroll-locked'));

		conferir();

		const observador = new MutationObserver(conferir);
		observador.observe(document.body, { attributes: true, attributeFilter: ['data-scroll-locked'] });

		return () => observador.disconnect();
	}, []);

	return (
		<FloatingPortal>
			<AnimatePresence mode='wait'>
				{ativo && tour && passo && alvo && !modalAberto ? (
					<CardDoPasso
						key={chaveDe(ativo)}
						alvo={alvo}
						passo={passo}
						index={index}
						total={total}
						unico={unico}
						focar={focoNoCard.current}
						aoNavegar={aoNavegar}
					/>
				) : null}
			</AnimatePresence>
		</FloatingPortal>
	);
}

export type TourProps = {
	abrirPainel?: AbrirPainel;
};

export function Tour(props: TourProps) {
	useTourExplicita();

	return <TourUI {...props} />;
}

export function TourAuto() {
	const explicitas = useTourExplicitas();
	const { openSlot } = useSlots();
	const { open } = useSubMenu();

	if (explicitas > 0) return null;

	return (
		<TourUI
			abrirPainel={(menu, slot) => {
				if (slot) openSlot(slot, menu);
				else if (menu) open(menu);
			}}
		/>
	);
}

export function TourTarget({ id, children }: { id: string; children: ReactNode }) {
	const ref = useRef<HTMLSpanElement>(null);

	useTourTarget(id, ref, true);

	return (
		<span ref={ref} data-tour-target={id} style={{ display: 'contents' }}>
			{children}
		</span>
	);
}
